import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";
import { POLL_INTERVALS, smartQueryDefaults, smartRefetchInterval } from '../utils/queryPolling';

// Query Keys
export const clientKeys = {
  all: ['clients'],
  lists: () => [...clientKeys.all, 'list'],
  list: (filters) => [...clientKeys.lists(), { filters }],
  details: () => [...clientKeys.all, 'detail'],
  detail: (id) => [...clientKeys.details(), id],
  stats: () => [...clientKeys.all, 'stats'],
};

export const clientApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/users', { 
      params: { ...params, user_type: 'client' }
    });
    
    // Handle Laravel pagination structure
    if (response.data && response.data.data && response.data.data.data) {
      // The data is nested: response.data.data.data
      const allUsers = response.data.data.data;
      // Filter data to only include clients (in case backend doesn't filter properly)
      const clients = allUsers.filter(user => user.user_type === 'client');
      
      return {
        success: true,
        message: 'Clients retrieved successfully',
        data: clients,
        pagination: {
          current_page: response.data.data.current_page,
          last_page: response.data.data.last_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total, // Use total count from API
          from: response.data.data.from,
          to: response.data.data.to
        }
      };
    }
    
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },
  updateStatus: async ({ id, status }) => {
    const response = await api.put(`/admin/users/${id}/status`, { 
      is_active: status
    });
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  // Get client statistics
  getStats: async () => {
    const response = await api.get('/admin/users/stats');
    return response.data;
  }
};

// Query hooks
export const useClients = (params = {}) => {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientApi.getAll(params),
    refetchInterval: smartRefetchInterval(POLL_INTERVALS.PRESENCE),
    staleTime: 15 * 1000,
    ...smartQueryDefaults,
  });
};

export const useClient = (id) => {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useClientStats = (options = {}) => {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: clientApi.getStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on mount if data is fresh
    ...options
  });
};

// Mutation hooks
export const useUpdateClientStatus = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: clientApi.updateStatus,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
};

export const useDeleteClient = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: clientApi.delete,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
};
